import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input, Button, type InputRef } from 'antd';
import { SearchOutlined, UpOutlined, DownOutlined, CloseOutlined } from '@ant-design/icons';
import Mark from 'mark.js';
import styles from './HelpPanelSearch.module.css';
import { useTranslation } from '@hooks/index';


type Props = {
  /** The container element to search within */
  searchContainer: HTMLElement | null;
  /** Whether the search box is visible */
  isVisible: boolean;
  /** Callback when search visibility changes */
  onVisibilityChange: (visible: boolean) => void;
  /** Callback when search results change */
  onSearchResultsChange?: (count: number, currentIndex: number) => void;
}

const HelpPanelSearch: React.FC<Props> = ({
  searchContainer,
  isVisible,
  onVisibilityChange,
  onSearchResultsChange,
}) => {
  const t = useTranslation('ServiceHelpPanel');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const inputRef = useRef<InputRef>(null);
  const markInstanceRef = useRef<Mark | null>(null);

  // Initialize mark.js instance
  useEffect(() => {
    if (searchContainer) {
      markInstanceRef.current = new Mark(searchContainer);
    }
    return () => {
      if (markInstanceRef.current) {
        markInstanceRef.current.unmark();
      }
    };
  }, [searchContainer]);

  const clearHighlights = useCallback(() => {
    if (markInstanceRef.current) {
      markInstanceRef.current.unmark();
    }
    setTotalResults(0);
    setCurrentIndex(0);
  }, []);

  // Highlight search results using mark.js
  const highlightSearchResults = useCallback((term: string) => {
    if (!markInstanceRef.current || !term.trim()) {
      clearHighlights();
      onSearchResultsChange?.(0, 0);
      return;
    }

    // Clear previous highlights
    markInstanceRef.current.unmark();

    markInstanceRef.current.mark(term, {
      className: styles.Highlight,
      caseSensitive: false,
      exclude: ['script', 'style', '.code-block'],
      done: (counter: number) => {
        setTotalResults(counter);
        setCurrentIndex(0);

        // Highlight the first result
        if (counter > 0) {
          const highlightElements = searchContainer?.querySelectorAll(`.${styles.Highlight}`);
          if (highlightElements && highlightElements[0]) {
            highlightElements[0].classList.add(styles.CurrentHighlight);

            // Scroll to the first result
            const element = highlightElements[0] as HTMLElement;
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          }
        }

        onSearchResultsChange?.(counter, 0);
      }
    });
  }, [searchContainer, clearHighlights, onSearchResultsChange]);

  // Navigate to next/previous result
  const navigateToResult = useCallback((direction: 'next' | 'previous') => {
    if (!searchContainer || totalResults === 0) { return; }

    // Get current highlighted elements from DOM
    const currentHighlights = searchContainer.querySelectorAll(`.${styles.Highlight}`);
    if (currentHighlights.length === 0) { return; }

    // Remove current highlight
    if (currentHighlights[currentIndex]) {
      currentHighlights[currentIndex].classList.remove(styles.CurrentHighlight);
    }

    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % currentHighlights.length;
    } else {
      newIndex = currentIndex === 0 ? currentHighlights.length - 1 : currentIndex - 1;
    }

    setCurrentIndex(newIndex);

    // Add highlight to new current element and scroll
    if (currentHighlights[newIndex]) {
      const element = currentHighlights[newIndex] as HTMLElement;
      element.classList.add(styles.CurrentHighlight);

      // Scroll to the element
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }

    onSearchResultsChange?.(totalResults, newIndex);
  }, [searchContainer, currentIndex, totalResults, onSearchResultsChange]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        navigateToResult('previous');
      } else {
        navigateToResult('next');
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeSearch();
    }
  };

  const closeSearch = () => {
    setSearchTerm('');
    clearHighlights();
    onVisibilityChange(false);
  };

  // Focus input when search becomes visible
  useEffect(() => {
    if (isVisible && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
    }
  }, [isVisible]);

  // Trigger search when search container becomes available (with debounce)
  useEffect(() => {
    if (searchContainer) {
      const timeoutId = setTimeout(() => {
        highlightSearchResults(searchTerm);
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [searchContainer, searchTerm, highlightSearchResults]);

  // Handle global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Check if Cmd/Ctrl+F is pressed and the search container exists (panel is open)
      if ((e.metaKey || e.ctrlKey) && e.key === 'f' && searchContainer) {
        e.preventDefault();
        e.stopPropagation();
        onVisibilityChange(true);
      }
    };

    // Add event listener to the document to catch keyboard shortcuts
    document.addEventListener('keydown', handleGlobalKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [searchContainer, onVisibilityChange]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.SearchContainer}>
      <div className={styles.SearchBox}>
        <Input
          ref={inputRef}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          placeholder={t('Search.Placeholder')}
          prefix={<SearchOutlined />}
          suffix={
            <div className={styles.SearchSuffix}>
              {totalResults > 0 && (
                <span className={styles.ResultCount}>
                  {currentIndex + 1}/{totalResults}
                </span>
              )}
              <Button
                type="text"
                size="small"
                icon={<UpOutlined />}
                onClick={() => navigateToResult('previous')}
                disabled={totalResults === 0}
                className={styles.NavButton}
              />
              <Button
                type="text"
                size="small"
                icon={<DownOutlined />}
                onClick={() => navigateToResult('next')}
                disabled={totalResults === 0}
                className={styles.NavButton}
              />
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={closeSearch}
                className={styles.CloseButton}
              />
            </div>
          }
          className={styles.SearchInput}
        />
      </div>
    </div>
  );
};

export default HelpPanelSearch;
